import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

/**
 * Brand-colored light/dark switch. Track shifts between a deep cobalt
 * night sky and a soft daylight gradient; the thumb slides and cross-fades
 * between sun/moon glyphs. Pure CSS transitions — no animation library needed.
 */
export default function ThemeToggle({ className = '' }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Modo claro' : 'Modo oscuro'}
      onClick={toggleTheme}
      data-testid="theme-toggle"
      className={`
        group relative inline-flex h-9 w-[68px] shrink-0 items-center rounded-full
        border bg-gradient-to-br from-[#dbeafe] to-[#e0f2fe]
        transition-colors duration-300 ease-out
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        focus-visible:ring-[#4660F5] focus-visible:ring-offset-transparent
        ${isDark
          ? 'border-white/10 shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]'
          : 'border-black/5 shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)]'}
        ${className}
      `}
    >
      {/* dark gradient overlay — crossfades via opacity in lockstep with the
          stars below. `bg-gradient-to-br` is a background-image, which CSS
          can't transition, so swapping it directly on the button snaps
          instantly; that left the fading star dots visibly stranded on the
          already-switched light track. Fading this layer in/out instead
          keeps the track and the stars in sync. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded-full bg-gradient-to-br from-[#0b0e21] to-[#151a3a] transition-opacity duration-300 ${isDark ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* faint stars, visible only on the dark track */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded-full overflow-hidden transition-opacity duration-300 ${isDark ? 'opacity-100' : 'opacity-0'}`}
      >
        <span className="absolute left-[10px] top-[7px] h-[2px] w-[2px] rounded-full bg-white/70" />
        <span className="absolute left-[18px] top-[20px] h-[2px] w-[2px] rounded-full bg-white/50" />
        <span className="absolute left-[8px] top-[22px] h-[1.5px] w-[1.5px] rounded-full bg-white/40" />
      </span>

      {/* thumb */}
      <span
        aria-hidden="true"
        className={`
          absolute top-1/2 left-1 -translate-y-1/2 flex h-7 w-7 items-center justify-center
          rounded-full shadow-md transition-all duration-300 ease-out
          group-active:scale-90
          ${isDark
            ? 'translate-x-[32px] bg-gradient-to-br from-[#4660F5] to-[#0409A3]'
            : 'translate-x-0 bg-gradient-to-br from-white to-[#fef3c7]'}
        `}
      >
        <Sun
          size={15}
          strokeWidth={2.25}
          className={`absolute text-[#f59e0b] transition-all duration-300 ${isDark ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'}`}
        />
        <Moon
          size={13}
          strokeWidth={2.25}
          className={`absolute text-[#e0e7ff] transition-all duration-300 ${isDark ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0'}`}
        />
      </span>
    </button>
  );
}
